import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, AlertTriangle, CheckCircle2, Send } from 'lucide-react';

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

function RenderBadge({ clip, gatewayUnavailable }) {
  if (gatewayUnavailable && clip.render_status === 'idle') return null;
  const map = {
    idle: { label: 'Not submitted', cls: 'bg-white/10 text-[#8A93A6]' },
    queued: { label: 'Queued', cls: 'bg-blue-500/10 text-blue-400' },
    processing: { label: 'Rendering…', cls: 'bg-cyan-500/10 text-cyan-400' },
    completed: { label: 'Rendered', cls: 'bg-green-500/10 text-green-400' },
    failed: { label: 'Failed', cls: 'bg-red-500/10 text-red-400' },
  };
  const cfg = map[clip.render_status] || map.idle;
  return <Badge className={`${cfg.cls} border-0`} data-testid={`render-status-badge-${clip.id}`}>{cfg.label}</Badge>;
}

/**
 * Final review step: approve/reject each clip, then submit only approved
 * clips to the existing /api/editor/jobs rendering pipeline. Clearly
 * distinguishes an unrendered editor decision from an actual B2-backed
 * rendered file, and never claims a render succeeded without the backend
 * confirming it.
 */
export function EditorReviewPanel({ clips, onToggleApprove, onSubmitRender, onDownload, gatewayUnavailable }) {
  if (clips.length === 0) {
    return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="review-panel-empty">No clips to review yet.</p>;
  }

  return (
    <div className="space-y-3" data-testid="editor-review-panel">
      {gatewayUnavailable && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2" data-testid="vultr-unavailable-notice">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            Rendering backend not connected yet (Vultr deployment pending). Your edit decisions are saved — submission will work once it's connected.
          </p>
        </div>
      )}

      {clips.map((clip) => (
        <div key={clip.id} className="p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`review-row-${clip.id}`}>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={clip.approved}
              onCheckedChange={() => onToggleApprove(clip.id)}
              data-testid={`review-approve-checkbox-${clip.id}`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{clip.name}</p>
              <p className="text-xs text-[#8A93A6]">
                {clip.output_type.toUpperCase()} · {fmt(clip.in_point)}–{fmt(clip.out_point)} · {clip.captions.length} caption{clip.captions.length !== 1 ? 's' : ''}
                {clip.brandkit_id ? ' · Branded' : ''}
                {clip.master_composition_slice ? ` · from Master v${clip.source_master_version} · ${clip.target_aspect_ratio || 'Master AR'}` : ''}
              </p>
            </div>
            <RenderBadge clip={clip} gatewayUnavailable={gatewayUnavailable} />
          </div>
          <div className="flex items-center gap-2 mt-2 pl-7">
            <Button
              size="sm" variant="outline" className="border-white/10 text-xs h-7"
              disabled={!clip.approved || ['queued', 'processing'].includes(clip.render_status)}
              onClick={() => onSubmitRender(clip.id)}
              data-testid={`submit-render-${clip.id}`}
            >
              {clip.render_status === 'processing' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Submit for Render
            </Button>
            {clip.render_status === 'completed' && (
              <Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={() => onDownload(clip.id)} data-testid={`download-rendered-${clip.id}`}>
                <Download className="h-3.5 w-3.5 mr-1.5" />Download Rendered Output
              </Button>
            )}
            {clip.render_status === 'failed' && clip.render_error && (
              <span className="text-xs text-red-400 truncate">{clip.render_error}</span>
            )}
            {clip.render_status === 'completed' && (
              <CheckCircle2 className="h-4 w-4 text-green-400" data-testid={`render-complete-icon-${clip.id}`} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
